import {
  exerciseHistory,
  GARMIN_CATEGORIES,
  listExercises,
  matchExercises,
  PB_LABEL,
  updateExercise,
  type ExerciseRow,
  type PbRow,
} from '@gymtv/data';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtWeight } from '../lib/format';
import { supabase } from '../lib/supabase';

const categoryLabel = (c: string | null) => (c ? (GARMIN_CATEGORIES.find(([v]) => v === c)?.[1] ?? c) : 'Auto');

/** The exercise library (docs/decisions/0016): one row per lift. */
export function Exercises() {
  const nav = useNavigate();
  const [rows, setRows] = useState<ExerciseRow[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    listExercises(supabase).then(setRows, (e) => setError(e.message));
  }, []);
  const shown = q.trim() ? matchExercises(rows ?? [], q, 500) : (rows ?? []);
  return (
    <>
      <div className="page-head">
        <h1>Exercises</h1>
        <input className="search" type="search" placeholder="Search exercises" aria-label="Search exercises" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <p className="muted">
        Each lift has one name, used by your programs, history and personal bests. Rename one here and it changes everywhere. New names
        typed in a program or workout are added automatically.
      </p>
      {error && <p className="error">{error}</p>}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Exercise</th>
              <th>Garmin category</th>
            </tr>
          </thead>
          <tbody>
            {rows && shown.length === 0 && (
              <tr>
                <td colSpan={2} className="muted">No exercise matches “{q}”. Type it into a program to add it.</td>
              </tr>
            )}
            {shown.map((e) => (
              <tr key={e.id} className="link" onClick={() => nav(`/exercises/${e.id}`)}>
                <td><b>{e.name}</b></td>
                <td className="muted">{categoryLabel(e.garmin_category)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

type History = Awaited<ReturnType<typeof exerciseHistory>>;

export function ExerciseDetail() {
  const { id = '' } = useParams();
  const { units } = useAuth();
  const [ex, setEx] = useState<ExerciseRow | null>(null);
  const [name, setName] = useState('');
  const [cat, setCat] = useState('');
  const [pbs, setPbs] = useState<PbRow[]>([]);
  const [history, setHistory] = useState<History | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('exercises')
      .select('id,name,garmin_category')
      .eq('id', id)
      .single()
      .then(({ data, error }) => {
        if (error) return setError('Exercise not found.');
        setEx(data as ExerciseRow);
        setName(data.name);
        setCat(data.garmin_category ?? '');
      });
    supabase
      .from('current_personal_bests')
      .select('*')
      .eq('exercise_id', id)
      .then(({ data }) => setPbs((data ?? []) as PbRow[]));
    exerciseHistory(supabase, id).then(setHistory, (e) => setError(e.message));
  }, [id]);

  async function save() {
    if (!ex) return;
    setError(null);
    setStatus('Saving…');
    try {
      const next = await updateExercise(supabase, ex.id, name, cat || null);
      setEx(next);
      setName(next.name);
      setStatus(next.name !== ex.name ? `Renamed everywhere to “${next.name}”.` : 'Saved.');
    } catch (e: any) {
      setStatus(null);
      setError(e.message);
    }
  }

  if (!ex) return error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>;
  const dirty = name.trim() !== ex.name || (cat || null) !== ex.garmin_category;
  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <Link to="/exercises" className="muted">← Exercises</Link>
          <h1>{ex.name}</h1>
        </div>
      </div>
      <form
        className="card form-row"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required />
        </label>
        <label className="field">
          Garmin category
          <select value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="">Auto (from the name)</option>
            {GARMIN_CATEGORIES.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="primary" disabled={!dirty || !name.trim()}>
          Save
        </button>
        {status && <span className="muted">{status}</span>}
      </form>
      {error && <p className="error">{error}</p>}

      <h2>Personal bests</h2>
      {pbs.length === 0 ? (
        <p className="muted">None yet.</p>
      ) : (
        <ul className="pb-list">
          {pbs.map((p) => (
            <li key={p.kind}>
              {PB_LABEL[p.kind]}: <b>{p.kind === 'max_reps' ? `${p.value} reps` : fmtWeight(p.value, units)}</b>{' '}
              <span className="muted">{fmtDate(p.achieved_at)}</span>
            </li>
          ))}
        </ul>
      )}

      <h2>History</h2>
      {history?.length === 0 && <p className="muted">No completed sets yet.</p>}
      <div className="table-wrap">
        <table className="data">
          <tbody>
            {history?.map((g) => (
              <tr key={g.session_id}>
                <td className="num">{fmtDate(g.started_at)}</td>
                <td>
                  <Link to={`/history/${g.session_id}`}>{g.title}</Link>
                </td>
                <td>{g.sets.map((s) => `${s.actual_reps ?? '–'}×${s.actual_weight ?? '–'}`).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
